import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { DynamicEntityDto, DynamicFormDto, DynamicFormElementDto, DynamicPayloadDto_Property, DynamicPayloadDto_PropertyValueList, DynamicPayloadDto_PropertyValueSubPayload } from '../../service/dto';
import { BehaviorSubject, catchError, map, Observable, of, Subscription, switchMap, timer } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { HttpDynamicService } from '../../service/http-dynamic.service';

export class FormControlWrapper {
	control!: FormControl;
	element!: DynamicFormElementDto;
	subscription?: Subscription;
	show!: boolean;
	autoTips!: Record<string, Record<string, string>>;
	groups!: FormGroupWrapper[];
	public toBackendPayload(): DynamicPayloadDto_Property {
		if (this.element.dataType != 'SetOfRecords') {
			return { propertyId: this.element.id, value: { dynamicPropertyValueType: 'simple', value: this.control.value } };
		}
		return {
			propertyId: this.element.id,
			value: {
				dynamicPropertyValueType: 'list',
				values: this.groups.map(item => item.toBackendPayload())
			}
		};
	}
}
export class FormGroupWrapper {
	instanceId?: number;
	formGroup!: FormGroup;
	formControls!: FormControlWrapper[];

	public toBackendPayload(): DynamicPayloadDto_PropertyValueSubPayload {
		return {
			dynamicPropertyValueType: 'subPayload',
			instanceId: this.instanceId,
			properties: this.formControls.map(item => item.toBackendPayload())
		};
	}
}

export abstract class AbstractDynamicForm {

	constructor(private _dynamicService: HttpDynamicService, private _fb: FormBuilder, private cdr: ChangeDetectorRef) { }

	buildFormControls(input: DynamicFormDto): FormControlWrapper[] {
		return input.elements.sort((i1, i2) => i1.orderIndex - i2.orderIndex).map(item => this.buildFormControlWrapper(item, input));
	}
	buildForm(controls: FormControlWrapper[]): FormGroup {
		const out = this._fb.group({});

		controls.forEach(item => {
			item.show = this.canShow(item, controls, item.control.value);
		});
		controls.filter(item => item.show == true).forEach(item => {
			out.addControl(AbstractDynamicForm.toControlKey(item.element), item.control);
		});
		controls.forEach(item => {
			this.registerChangeListeners(out, item, controls);
		});


		return out;
	}
	private canShow(wrapper: FormControlWrapper, allWrappers: FormControlWrapper[], value: string): boolean {
		if (wrapper.element.dependencyId == undefined) {
			return true;
		}
		const parent = allWrappers.filter(item => item.element.id == wrapper.element.dependencyId)[0];

		return parent.control.value == wrapper.element.dependencyValue;
	}

	private registerChangeListeners(form: FormGroup, wrapper: FormControlWrapper, allWrappers: FormControlWrapper[]) {
		const dependents = allWrappers.filter(i => i.element.dependencyId == wrapper.element.id);
		if (dependents.length == 0) {
			return;
		}
		wrapper.subscription = wrapper.control.valueChanges.subscribe(item => {
			wrapper.show = this.canShow(wrapper, allWrappers, item);

			console.log(`Changed: [${wrapper.element.property}] ${wrapper.show}`, item)

			dependents.forEach(i => {
				if (i.element.dependencyValue == item) {
					if (!form.contains(AbstractDynamicForm.toControlKey(i.element))) {
						form.addControl(AbstractDynamicForm.toControlKey(i.element), i.control);
					}
				} else {
					if (form.contains(AbstractDynamicForm.toControlKey(i.element))) {
						form.removeControl(AbstractDynamicForm.toControlKey(i.element));
					}
				}
			});
			this.cdr.detectChanges();
		});
	}
	public static toControlKey(input: DynamicFormElementDto): string {
		return 'f_' + input.id;
	}

	protected buildFormControlWrapper(input: DynamicFormElementDto, dynamicForm: DynamicFormDto): FormControlWrapper {
		const control: FormControl = this._fb.control(input.value, this.buildSyncValidators(input), this.buildAsyncValidators(input, dynamicForm.id));
		(control as any) ['custom_indicator_comment']=input.sectionName + " - " + input.name;

		const out = new FormControlWrapper();
		out.control = control;
		out.element = input;
		out.show = false;
		out.autoTips = {
			default: {
				required: input.errorMessageRequired ?? `Required`,
				requiredTrue: input.errorMessageRequired ?? `Required`,
				notUnique: input.errorMessageUnique ?? `Not unique`,
				email: `Invalid email address`,
				number: `invlaid number`,
				regex: input.errorMessageRegex ?? `Invalid pattern`,
				minlength: input.errorMessageMinValue ?? `Too small`,
				maxlength: input.errorMessageMaxValue ?? `Too Large`,
				min: input.errorMessageMinValue ?? `Too small`,
				max: input.errorMessageMaxValue ?? `Too Large`
			},
		};
		if (input.dataType != 'SetOfRecords') {
			out.groups = [];
			return out;
		}
		if (!input.value) {
			out.groups = [];
			return out;
		}
		out.groups = (input.value as DynamicFormDto[]).map(item => {
			const fgw = new FormGroupWrapper();
			fgw.instanceId = item.id;
			fgw.formGroup = this._fb.group({});
			fgw.formControls = item.elements.sort((i1, i2) => i1.orderIndex! - i2.orderIndex!).map(i => this.buildFormControlWrapper(i, item));
			fgw.formControls.forEach(item => fgw.formGroup.addControl(AbstractDynamicForm.toControlKey(item.element), item.control));
			return fgw;
		}).sort((i1, i2) => i1.instanceId! - i2.instanceId!);
		return out;
	}

	private buildSyncValidators(input: DynamicFormElementDto): ValidatorFn[] {
		const validators: ValidatorFn[] = [];

		if (input.required == true) {
			validators.push(Validators.required);
		}

		// Regex
		if (input.regex) {
			const regex = new RegExp(input.regex);
			validators.push((control: AbstractControl) => {
				if (control.value == null || control.value === '') return null; // ignore empty (handled by required)
				return regex.test(control.value) ? null : { regex: input.errorMessageRegex || 'Invalid format' };
			});
		}
		switch (input.dataType) {
			case 'Text':
				validators.push(...this.buildSyncValidatorText(input));
				break;
			case 'Password':
				validators.push(...this.buildSyncValidatorText(input));
				break;
			case 'Number':
				validators.push(...this.buildSyncValidatorNumber(input));
				break;
			case 'Email':
				validators.push(...this.buildSyncValidatorEmail(input));
				break;
			case 'Msisdn':
				validators.push(...this.buildSyncValidatorMsisdn(input));
				break;
			case 'RequiredCheckbox':
				validators.push(Validators.requiredTrue);
				break;
			// case 'PastDate':
			// 	validators.push(...this.buildSyncValidatorDate(input));
			// case 'FutureDate':
			// 	validators.push(...this.buildSyncValidatorDate(input));
			// case 'PastDateTime':
			// 	validators.push(...this.buildSyncValidatorDate(input));
			// case 'FutureDateTime':
			// 	validators.push(...this.buildSyncValidatorDate(input));
		}
		return validators;
	}

	private buildAsyncValidators(input: DynamicFormElementDto, instanceId: number | undefined): AsyncValidatorFn[] {
		const validators: AsyncValidatorFn[] = [];

		if (input.isUnique == true) {
			validators.push(this.uniqueValidator(this._dynamicService, input.id, instanceId))
		}
		return validators;
	}
	uniqueValidator(service: HttpDynamicService, propertyId: number, instanceId: number | undefined): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			if (!control.value) {
				return of(null); // don't validate empty
			}

			// debounce with timer
			return timer(400).pipe(
				switchMap(() => service.checkUnique(propertyId, instanceId, control.value)),
				map(res => (res.unique ? null : { notUnique: true })),
				catchError(() => of(null))
			);
		};
	}

	private buildSyncValidatorText(input: DynamicFormElementDto): ValidatorFn[] {
		const validators: ValidatorFn[] = [];
		if (input.minValue) {
			validators.push(Validators.minLength(input.minValue));
		}
		if (input.maxValue) {
			validators.push(Validators.maxLength(input.maxValue));
		}
		return validators;
	}
	private buildSyncValidatorNumber(input: DynamicFormElementDto): ValidatorFn[] {
		const validators: ValidatorFn[] = [];

		if (input.minValue) {
			validators.push(Validators.min(input.minValue));
		}
		if (input.maxValue) {
			validators.push(Validators.max(input.maxValue));
		}

		return validators;
	}
	private buildSyncValidatorEmail(input: DynamicFormElementDto): ValidatorFn[] {
		return [Validators.email];
	}
	private buildSyncValidatorMsisdn(input: DynamicFormElementDto): ValidatorFn[] {
		const validators: ValidatorFn[] = [];
		// naive: must be digits only
		validators.push((c: AbstractControl) => {
			if (!c.value) return null;
			return /^\+?\d+$/.test(c.value)
				? null
				: { msisdn: 'Invalid phone number format' };
		});
		return validators;
	}
	hasControl(form: FormGroup, item: FormControlWrapper): boolean {
		return form.contains(AbstractDynamicForm.toControlKey(item.element)) ?? false;
	}
}
